import { useState, useEffect } from 'react';

import { motion } from 'framer-motion';

import { 

  BookOpen, 

  Star, 

  Trophy, 

  Video, 

  Shield, 

  BarChart3, 

  CheckCircle,

  ChevronRight,

  Menu,

  X,

  Sparkles,

  Heart,

  Gamepad2,

  GraduationCap,

  Pencil,

  Lightbulb,

  Rocket,

  Music,

  Palette,

  Phone,

  Mail

} from 'lucide-react';

import { useAuth } from '../context/AuthContext';

import { useNavigate } from 'react-router-dom';

import { AuthModals } from './AuthModals';



// Image URLs from design guidelines

const IMAGES = {

  tutor: "/images/tutor.jpg",

  heroKids: "/images/hero-kids.jpg",

  parentChild: "/images/parent-child.jpg",

};



// Animation variants

const floatAnimation = {

  animate: { y: [0, -15, 0] },

  transition: { repeat: Infinity, duration: 4, ease: "easeInOut" }

};



const bounceAnimation = {

  animate: { y: [0, -25, 0] },

  transition: { repeat: Infinity, duration: 2, ease: "easeOut" }

};



const waveAnimation = {

  animate: { rotate: [0, 15, -15, 0] },

  transition: { repeat: Infinity, duration: 2.5, ease: "easeInOut" }

};



const pulseAnimation = {

  animate: { scale: [1, 1.05, 1] },

  transition: { repeat: Infinity, duration: 3, ease: "easeInOut" }

};



// Floating Icon Component - Clean colorful icons

function FloatingIcon({ icon: Icon, className, color, bgColor, animationType = 'float', size = 40 }) {

  const animations = {

    float: floatAnimation,

    bounce: bounceAnimation,

    wave: waveAnimation,

    pulse: pulseAnimation

  };



  const anim = animations[animationType];



  return (

    <motion.div

      className={`absolute ${className} ${bgColor} rounded-full p-2 shadow-lg`}

      style={{ width: size, height: size }}

      animate={anim.animate}

      transition={anim.transition}

    >

      <Icon className={`w-full h-full ${color}`} />

    </motion.div>

  );

}



// Navigation Component

function Navigation({ onAuthClick, user, onLogout }) {

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();

  const goDashboard = () => {

    navigate(user?.role === 'admin' ? '/admin' : '/dashboard');

  };



  return (

    <motion.nav 

      initial={{ y: -100 }}

      animate={{ y: 0 }}

      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-blue/20"

      data-testid="navigation"

    >

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}

          <motion.button 

            className="flex items-center gap-2 cursor-pointer"

            whileHover={{ scale: 1.05 }}

            onClick={() => navigate('/')}

            data-testid="brand-logo-link"

          >

            <Sparkles className="w-8 h-8 text-sunset-orange" />

            <span className="font-fredoka text-2xl md:text-3xl font-bold text-gray-800" data-testid="brand-logo">

              Craven-it

            </span>

          </motion.button>



          {/* Desktop Menu */}

          <div className="hidden md:flex items-center gap-8">

            <a href="#features" className="font-nunito font-semibold text-gray-700 hover:text-sunset-orange transition-colors">Features</a>

            <a href="#how-it-works" className="font-nunito font-semibold text-gray-700 hover:text-sunset-orange transition-colors">How It Works</a>

            <a href="#parents" className="font-nunito font-semibold text-gray-700 hover:text-sunset-orange transition-colors">For Parents</a>



            {user ? (

              <div className="flex items-center gap-3">

                <span className="font-nunito text-gray-700 hidden lg:inline">Hi, {user.name}!</span>

                <motion.button

                  whileHover={{ scale: 1.05, y: -2 }}

                  whileTap={{ scale: 0.95 }}

                  onClick={goDashboard}

                  className="bg-sunset-orange text-white px-6 py-2 rounded-full font-fredoka font-bold shadow-md hover:shadow-lg transition-all"

                  data-testid="nav-dashboard-btn"

                >

                  {user?.role === 'admin' ? 'Admin Dashboard' : 'Dashboard'}

                </motion.button>

                <motion.button

                  whileHover={{ scale: 1.05 }}

                  whileTap={{ scale: 0.95 }}

                  onClick={onLogout}

                  className="bg-gray-200 text-gray-700 px-5 py-2 rounded-full font-fredoka font-bold"

                  data-testid="logout-button"

                >

                  Logout

                </motion.button>

              </div>

            ) : (

              <div className="flex items-center gap-2">

                <motion.button

                  whileHover={{ scale: 1.05 }}

                  whileTap={{ scale: 0.95 }}

                  onClick={() => onAuthClick('login')}

                  className="text-sunset-orange px-5 py-2.5 rounded-full font-fredoka font-bold hover:bg-sunset-orange/10 transition-all"

                  data-testid="nav-login-button"

                >

                  Login

                </motion.button>

                <motion.button

                  whileHover={{ scale: 1.05, y: -2 }}

                  whileTap={{ scale: 0.95 }}

                  onClick={() => onAuthClick('register')}

                  className="bg-sunset-orange text-white px-7 py-2.5 rounded-full font-fredoka font-bold shadow-lg hover:shadow-xl transition-all"

                  data-testid="nav-signup-button"

                >

                  Sign Up

                </motion.button>

              </div>

            )}

          </div>



          {/* Mobile Menu Button */}

          <button 

            className="md:hidden p-2"

            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}

            data-testid="mobile-menu-button"

          >

            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}

          </button>

        </div>



        {/* Mobile Menu */}

        {mobileMenuOpen && (

          <motion.div

            initial={{ opacity: 0, height: 0 }}

            animate={{ opacity: 1, height: 'auto' }}

            exit={{ opacity: 0, height: 0 }}

            className="md:hidden py-4 space-y-4"

          >

            <a href="#features" className="block font-nunito font-semibold text-gray-700 py-2">Features</a>

            <a href="#how-it-works" className="block font-nunito font-semibold text-gray-700 py-2">How It Works</a>

            <a href="#parents" className="block font-nunito font-semibold text-gray-700 py-2">For Parents</a>

            {user ? (

              <>

                <button

                  onClick={() => { navigate(user?.role === 'admin' ? '/admin' : '/dashboard'); setMobileMenuOpen(false); }}

                  className="w-full bg-sunset-orange text-white py-3 rounded-full font-fredoka font-bold"

                  data-testid="nav-dashboard-btn-mobile"

                >

                  {user?.role === 'admin' ? 'Admin Dashboard' : 'Dashboard'}

                </button>

                <button

                  onClick={onLogout}

                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-full font-fredoka font-bold"

                >

                  Logout

                </button>

              </>

            ) : (

              <>

                <button

                  onClick={() => { onAuthClick('login'); setMobileMenuOpen(false); }}

                  className="w-full text-sunset-orange border-2 border-sunset-orange py-3 rounded-full font-fredoka font-bold"

                  data-testid="nav-login-button-mobile"

                >

                  Login

                </button>

                <button

                  onClick={() => { onAuthClick('register'); setMobileMenuOpen(false); }}

                  className="w-full bg-sunset-orange text-white py-3 rounded-full font-fredoka font-bold"

                  data-testid="nav-signup-button-mobile"

                >

                  Sign Up

                </button>

              </>

            )}

          </motion.div>

        )}

      </div>

    </motion.nav>

  );

}



// Hero Section

function HeroSection({ onAuthClick }) {

  return (

    <section 

      className="relative bg-cream pt-28 md:pt-32 pb-16 overflow-hidden"

      data-testid="hero-section"

    >

      {/* Background decorations */}

      <div className="absolute inset-0 overflow-hidden">

        <motion.div 

          className="absolute top-20 right-10 w-40 h-40 bg-sky-blue/20 rounded-full blur-3xl"

          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}

          transition={{ duration: 6, repeat: Infinity }}

        />

        <motion.div 

          className="absolute bottom-20 left-10 w-60 h-60 bg-sunny-yellow/20 rounded-full blur-3xl"

          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}

          transition={{ duration: 8, repeat: Infinity }}

        />

        <motion.div 

          className="absolute top-1/2 right-1/4 w-32 h-32 bg-apple-green/15 rounded-full blur-2xl"

          animate={{ scale: [1, 1.2, 1] }}

          transition={{ duration: 5, repeat: Infinity }}

        />

      </div>



      {/* Floating icons - clean decorations */}

      <FloatingIcon icon={BookOpen} className="top-28 right-[8%] hidden lg:block" color="text-sky-blue" bgColor="bg-white" animationType="float" size={50} />

      <FloatingIcon icon={Pencil} className="bottom-32 left-[6%] hidden lg:block" color="text-sunset-orange" bgColor="bg-white" animationType="bounce" size={45} />

      <FloatingIcon icon={Lightbulb} className="top-1/3 left-[4%] hidden xl:block" color="text-sunny-yellow" bgColor="bg-white" animationType="wave" size={45} />



      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* Left Content */}

          <motion.div

            initial={{ opacity: 0, x: -50 }}

            animate={{ opacity: 1, x: 0 }}

            transition={{ duration: 0.8 }}

            className="text-center md:text-left"

          >

            <motion.div

              initial={{ opacity: 0, y: 20 }}

              animate={{ opacity: 1, y: 0 }}

              transition={{ delay: 0.2 }}

              className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md mb-6"

            >

              <Star className="w-5 h-5 text-sunny-yellow fill-sunny-yellow" />

              <span className="font-nunito font-semibold text-gray-700 text-sm md:text-base">Fun Learning Platform for Kids</span>

            </motion.div>



            <h1 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 leading-tight mb-5">

              Where Learning

              <span className="block text-sunset-orange">Feels Like Play!</span>

            </h1>



            <p className="font-nunito text-base md:text-lg text-gray-600 mb-6 max-w-lg mx-auto md:mx-0">

              Fun, small-group tutoring for K-2 students to build confidence in reading and math. Watch your child shine!

            </p>



            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">

              <motion.button

                whileHover={{ scale: 1.05, y: -3 }}

                whileTap={{ scale: 0.95 }}

                onClick={() => onAuthClick('register')}

                className="bg-sunset-orange text-white px-8 py-3 rounded-full font-fredoka font-bold text-base shadow-[0_4px_14px_0_rgb(253,94,83,0.39)] hover:shadow-[0_6px_20px_rgb(253,94,83,0.5)] transition-all flex items-center justify-center gap-2"

                data-testid="hero-cta-button"

              >

                Start Learning <ChevronRight className="w-5 h-5" />

              </motion.button>

              <motion.button

                whileHover={{ scale: 1.05 }}

                whileTap={{ scale: 0.95 }}

                className="border-2 border-sky-blue text-gray-700 px-8 py-3 rounded-full font-fredoka font-bold text-base hover:bg-sky-blue/10 transition-all"

                data-testid="hero-secondary-button"

              >

                Watch Demo

              </motion.button>

            </div>

          </motion.div>



          {/* Right Content - Kids Illustration */}

          <motion.div

            initial={{ opacity: 0, x: 50 }}

            animate={{ opacity: 1, x: 0 }}

            transition={{ duration: 0.8, delay: 0.3 }}

            className="relative flex justify-center"

          >

            <div className="relative w-full max-w-lg">

              {/* Decorative background shapes */}

              <motion.div 

                className="absolute -top-6 -left-6 w-24 h-24 bg-sunny-yellow/40 rounded-full blur-sm"

                animate={{ scale: [1, 1.1, 1] }}

                transition={{ duration: 4, repeat: Infinity }}

              />

              <motion.div 

                className="absolute -bottom-4 -right-4 w-20 h-20 bg-apple-green/40 rounded-full blur-sm"

                animate={{ scale: [1, 1.15, 1] }}

                transition={{ duration: 5, repeat: Infinity }}

              />

              <motion.div 

                className="absolute top-1/2 -right-8 w-16 h-16 bg-sky-blue/30 rounded-full blur-sm"

                animate={{ scale: [1, 1.2, 1] }}

                transition={{ duration: 3.5, repeat: Infinity }}

              />



              {/* Main kids illustration */}

              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">

                <img 

                  src={IMAGES.heroKids} 

                  alt="Happy kids learning together" 

                  className="w-full h-auto"

                  fetchPriority="high"

                  loading="eager"

                  data-testid="hero-kids-image"

                />

              </div>



              {/* Floating icon decorations around the image */}

              <motion.div

                className="absolute -top-3 -right-3 w-12 h-12 bg-white rounded-full shadow-lg p-2.5 flex items-center justify-center"

                animate={{ y: [0, -8, 0], rotate: [0, 5, -5, 0] }}

                transition={{ duration: 3, repeat: Infinity }}

              >

                <Star className="w-full h-full text-sunny-yellow fill-sunny-yellow" />

              </motion.div>



              <motion.div

                className="absolute -bottom-2 -left-2 w-10 h-10 bg-white rounded-full shadow-lg p-2 flex items-center justify-center"

                animate={{ y: [0, -6, 0] }}

                transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}

              >

                <Heart className="w-full h-full text-sunset-orange fill-sunset-orange" />

              </motion.div>



              <motion.div

                className="absolute top-1/3 -left-4 w-9 h-9 bg-white rounded-full shadow-lg p-2 flex items-center justify-center"

                animate={{ x: [0, 5, 0], y: [0, -5, 0] }}

                transition={{ duration: 3.5, repeat: Infinity, delay: 0.3 }}

              >

                <Rocket className="w-full h-full text-sky-blue" />

              </motion.div>



              <motion.div

                className="absolute -bottom-1 right-1/4 w-9 h-9 bg-white rounded-full shadow-lg p-2 flex items-center justify-center"

                animate={{ y: [0, -7, 0] }}

                transition={{ duration: 2.8, repeat: Infinity, delay: 0.7 }}

              >

                <Trophy className="w-full h-full text-apple-green" />

              </motion.div>



              {/* Floating badge */}

              <motion.div

                initial={{ opacity: 0, scale: 0 }}

                animate={{ opacity: 1, scale: 1 }}

                transition={{ delay: 1 }}

                className="absolute -bottom-5 -right-2 bg-white px-4 py-2 rounded-2xl shadow-xl"

              >

                <div className="flex items-center gap-2">

                  <div className="w-8 h-8 bg-apple-green/20 rounded-full flex items-center justify-center">

                    <BookOpen className="w-4 h-4 text-apple-green" />

                  </div>

                  <div>

                    <p className="font-fredoka font-bold text-gray-800 text-sm">Fun Learning</p>

                    <p className="font-nunito text-xs text-gray-600">Interactive Lessons</p>

                  </div>

                </div>

              </motion.div>



              {/* Stars decoration */}

              <motion.div

                className="absolute top-4 left-1/4"

                animate={{ rotate: 360, scale: [1, 1.2, 1] }}

                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}

              >

                <Star className="w-5 h-5 text-sunny-yellow fill-sunny-yellow" />

              </motion.div>

            </div>

          </motion.div>

        </div>

      </div>



      {/* Wavy divider */}

      <div className="absolute bottom-0 left-0 right-0">

        <svg viewBox="0 0 1440 60" fill="none" xmlns="http\://www\.w3.org/2000/svg" className="w-full">

          <path d="M0 60L60 52.5C120 45 240 30 360 22.5C480 15 600 15 720 22.5C840 30 960 45 1080 52.5C1200 60 1320 60 1380 60H1440V60H0Z" fill="white"/>

        </svg>

      </div>

    </section>

  );

}



// Features Section

function FeaturesSection() {

  const features = [

    {

      icon: BookOpen,

      title: "Interactive Lessons",

      description: "Engaging video lessons designed specifically for young minds with animations and storytelling.",

      color: "bg-sky-blue/20",

      iconColor: "text-sky-blue"

    },

    {

      icon: Gamepad2,

      title: "Learning Games",

      description: "Educational games that make learning fun while building essential skills and knowledge.",

      color: "bg-sunny-yellow/20",

      iconColor: "text-sunny-yellow"

    },

    {

      icon: Video,

      title: "Live Classes",

      description: "Join live interactive sessions with Tracey and learn in a fun, supportive environment.",

      color: "bg-apple-green/20",

      iconColor: "text-apple-green"

    },

    {

      icon: Trophy,

      title: "Rewards & Badges",

      description: "Earn exciting rewards, badges, and certificates as you complete lessons and challenges.",

      color: "bg-sunset-orange/20",

      iconColor: "text-sunset-orange"

    },

    {

      icon: Shield,

      title: "Safe Environment",

      description: "A secure, ad-free platform designed with child safety as the top priority.",

      color: "bg-grass-green/20",

      iconColor: "text-grass-green"

    },

    {

      icon: BarChart3,

      title: "Progress Tracking",

      description: "Parents can monitor learning progress with detailed reports and insights.",

      color: "bg-sky-blue/20",

      iconColor: "text-sky-blue"

    }

  ];



  return (

    <section id="features" className="py-12 md:py-16 bg-white relative overflow-hidden" data-testid="features-section">

      {/* Floating decoration */}

      <FloatingIcon icon={Sparkles} className="top-10 right-[5%] hidden lg:block" color="text-sunset-orange" bgColor="bg-white" animationType="bounce" size={45} />



      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <motion.div

          initial={{ opacity: 0, y: 30 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="text-center mb-10"

        >

          <span className="inline-block bg-sky-blue/20 text-sky-blue px-4 py-2 rounded-full font-nunito font-semibold mb-4">

            Amazing Features

          </span>

          <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-gray-800 mb-4">

            Everything Kids Need to Learn & Grow

          </h2>

          <p className="font-nunito text-lg text-gray-600 max-w-2xl mx-auto">

            Discover our carefully crafted features designed to make learning an enjoyable adventure for every child.

          </p>

        </motion.div>



        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

          {features.map((feature, index) => (

            <motion.div

              key={index}

              initial={{ opacity: 0, y: 30 }}

              whileInView={{ opacity: 1, y: 0 }}

              viewport={{ once: true }}

              transition={{ delay: index * 0.1 }}

              whileHover={{ y: -8, scale: 1.02 }}

              className="bg-white border-2 border-sky-blue/20 rounded-3xl p-8 hover:border-sky-blue hover:shadow-xl transition-all relative group"

              data-testid={`feature-card-${index}`}

            >

              <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>

                <feature.icon className={`w-8 h-8 ${feature.iconColor}`} />

              </div>



              <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-3">

                {feature.title}

              </h3>

              <p className="font-nunito text-gray-600">

                {feature.description}

              </p>

            </motion.div>

          ))}

        </div>

      </div>

    </section>

  );

}



// Gamification Section

function GamificationSection() {

  return (

    <section className="py-12 md:py-16 bg-sky-blue relative overflow-hidden" data-testid="gamification-section">

      {/* Background decorations */}

      <motion.div 

        className="absolute top-10 left-10 w-20 h-20 bg-sunny-yellow/30 rounded-full"

        animate={{ scale: [1, 1.2, 1] }}

        transition={{ duration: 3, repeat: Infinity }}

      />

      <motion.div 

        className="absolute bottom-10 right-10 w-32 h-32 bg-white/20 rounded-full"

        animate={{ scale: [1, 1.3, 1] }}

        transition={{ duration: 4, repeat: Infinity }}

      />



      {/* Floating icons */}

      <FloatingIcon icon={Trophy} className="top-20 right-[10%] hidden lg:block" color="text-sunny-yellow" bgColor="bg-white" animationType="bounce" size={55} />

      <FloatingIcon icon={Star} className="bottom-20 left-[5%] hidden lg:block" color="text-white" bgColor="bg-sunny-yellow" animationType="float" size={50} />



      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">

        <div className="grid md:grid-cols-2 gap-8 items-center">

          <motion.div

            initial={{ opacity: 0, x: -50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

          >

            <span className="inline-block bg-white/30 text-white px-4 py-2 rounded-full font-nunito font-semibold mb-4">

              Rewards System

            </span>

            <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-white mb-6">

              Learn, Play & Earn Amazing Rewards!

            </h2>

            <p className="font-nunito text-lg text-white/90 mb-8">

              Our gamification system keeps kids motivated and excited about learning. Every lesson completed, quiz passed, and challenge conquered earns points and unlocks cool rewards!

            </p>



            <div className="space-y-4">

              {[

                { icon: Star, text: "Earn stars for completing lessons" },

                { icon: Trophy, text: "Unlock badges and achievements" },

                { icon: Heart, text: "Collect points for special rewards" }

              ].map((item, index) => (

                <motion.div

                  key={index}

                  initial={{ opacity: 0, x: -20 }}

                  whileInView={{ opacity: 1, x: 0 }}

                  viewport={{ once: true }}

                  transition={{ delay: index * 0.1 }}

                  className="flex items-center gap-4 bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4"

                >

                  <div className="w-10 h-10 bg-sunny-yellow rounded-full flex items-center justify-center">

                    <item.icon className="w-5 h-5 text-gray-800" />

                  </div>

                  <span className="font-nunito font-semibold text-white">{item.text}</span>

                </motion.div>

              ))}

            </div>

          </motion.div>



          <motion.div

            initial={{ opacity: 0, x: 50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="relative"

          >

            {/* Stats showcase */}

            <div className="bg-white rounded-3xl p-8 shadow-2xl">

              <div className="text-center mb-8">

                <motion.div

                  animate={{ scale: [1, 1.1, 1] }}

                  transition={{ duration: 2, repeat: Infinity }}

                  className="inline-flex items-center justify-center w-20 h-20 bg-sunny-yellow rounded-full mb-4"

                >

                  <Trophy className="w-10 h-10 text-gray-800" />

                </motion.div>

                <h3 className="font-fredoka text-2xl font-bold text-gray-800">Trusted by Families</h3>

              </div>



              {/* Stat tiles */}

              <div className="space-y-4">

                <motion.div

                  initial={{ opacity: 0, y: 20 }}

                  whileInView={{ opacity: 1, y: 0 }}

                  viewport={{ once: true }}

                  transition={{ duration: 0.6, delay: 0.2 }}

                  className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FFE4E6] to-[#FFD5DC] p-5 flex items-center gap-4"

                  data-testid="stat-happy-families"

                >

                  <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 shadow-sm">

                    <Heart className="w-7 h-7 text-[#FD5E53]" />

                  </div>

                  <div>

                    <div className="font-fredoka text-3xl font-bold text-gray-800 leading-none">100+</div>

                    <div className="font-nunito text-sm font-semibold text-gray-600 mt-1">Happy Families</div>

                  </div>

                </motion.div>

                <motion.div

                  initial={{ opacity: 0, y: 20 }}

                  whileInView={{ opacity: 1, y: 0 }}

                  viewport={{ once: true }}

                  transition={{ duration: 0.6, delay: 0.4 }}

                  className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#DCFCE7] to-[#BBF7D0] p-5 flex items-center gap-4"

                  data-testid="stat-grades-supported"

                >

                  <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 shadow-sm">

                    <GraduationCap className="w-7 h-7 text-[#16A34A]" />

                  </div>

                  <div>

                    <div className="font-fredoka text-3xl font-bold text-gray-800 leading-none">K–2</div>

                    <div className="font-nunito text-sm font-semibold text-gray-600 mt-1">Grades Supported</div>

                  </div>

                </motion.div>

              </div>



              {/* Stars accent */}

              <div className="mt-8 flex items-center justify-center gap-2">

                {[1, 2, 3, 4, 5].map((_, i) => (

                  <motion.div

                    key={i}

                    initial={{ opacity: 0, scale: 0 }}

                    whileInView={{ opacity: 1, scale: 1 }}

                    viewport={{ once: true }}

                    transition={{ delay: 0.8 + i * 0.1 }}

                  >

                    <Star className="w-8 h-8 text-sunny-yellow fill-sunny-yellow" />

                  </motion.div>

                ))}

              </div>

            </div>

          </motion.div>

        </div>

      </div>

    </section>

  );

}



// How It Works Section

function HowItWorksSection({ onAuthClick }) {

  const steps = [

    {

      number: "01",

      icon: Pencil,

      title: "Sign Up",

      description: "Create a free account in just a few clicks. It's quick, easy, and safe!",

      color: "bg-sunset-orange"

    },

    {

      number: "02",

      icon: BookOpen,

      title: "Start Learning",

      description: "Explore fun lessons, watch videos, and play educational games with Tracey.",

      color: "bg-apple-green"

    },

    {

      number: "03",

      icon: Trophy,

      title: "Earn Rewards",

      description: "Complete challenges, collect points, and unlock awesome badges!",

      color: "bg-sunny-yellow"

    }

  ];



  return (

    <section id="how-it-works" className="py-12 md:py-16 bg-white relative overflow-hidden" data-testid="how-it-works-section">

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <motion.div

          initial={{ opacity: 0, y: 30 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="text-center mb-10"

        >

          <span className="inline-block bg-grass-green/20 text-grass-green px-4 py-2 rounded-full font-nunito font-semibold mb-4">

            Easy as 1-2-3

          </span>

          <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-gray-800 mb-4">

            How It Works

          </h2>

          <p className="font-nunito text-lg text-gray-600 max-w-2xl mx-auto">

            Getting started with Craven-it is super easy! Follow these simple steps to begin your learning adventure.

          </p>

        </motion.div>



        <div className="relative">

          {/* Connection line - hidden on mobile */}

          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-sunset-orange via-apple-green to-sunny-yellow transform -translate-y-1/2" style={{ top: '80px' }} />



          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">

            {steps.map((step, index) => (

              <motion.div

                key={index}

                initial={{ opacity: 0, y: 30 }}

                whileInView={{ opacity: 1, y: 0 }}

                viewport={{ once: true }}

                transition={{ delay: index * 0.2 }}

                className="relative text-center"

                data-testid={`step-${index + 1}`}

              >

                {/* Step number */}

                <motion.div 

                  className={`relative z-10 w-20 h-20 ${step.color} rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg`}

                  whileHover={{ scale: 1.1, rotate: 5 }}

                >

                  <span className="font-fredoka text-2xl font-bold text-white">{step.number}</span>



                  {/* Floating icon */}

                  <motion.div

                    className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center"

                    animate={{ y: [0, -6, 0], rotate: [0, 10, 0] }}

                    transition={{ duration: 3, repeat: Infinity }}

                  >

                    <step.icon className="w-5 h-5 text-gray-700" />

                  </motion.div>

                </motion.div>



                <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-3">

                  {step.title}

                </h3>

                <p className="font-nunito text-gray-600">

                  {step.description}

                </p>

              </motion.div>

            ))}

          </div>

        </div>



        {/* CTA */}

        <motion.div

          initial={{ opacity: 0, y: 30 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="text-center mt-12"

        >

          <motion.button

            whileHover={{ scale: 1.05, y: -3 }}

            whileTap={{ scale: 0.95 }}

            onClick={() => onAuthClick('register')}

            className="bg-sunset-orange text-white px-10 py-4 rounded-full font-fredoka font-bold text-lg shadow-[0_4px_14px_0_rgb(253,94,83,0.39)] hover:shadow-[0_6px_20px_rgb(253,94,83,0.5)] transition-all"

            data-testid="how-it-works-cta"

          >

            Start Your Journey Now

          </motion.button>

        </motion.div>

      </div>

    </section>

  );

}



// Schedule Section

function ScheduleSection() {

  return (

    <section className="py-12 md:py-16 bg-cream relative overflow-hidden" data-testid="schedule-section">

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <motion.div

          initial={{ opacity: 0, y: 30 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="text-center mb-10"

        >

          <span className="inline-block bg-sky-blue/20 text-sky-blue px-4 py-2 rounded-full font-nunito font-semibold mb-4">

            Session Times

          </span>

          <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-gray-800 mb-4">

            Tutoring Schedule

          </h2>

          <p className="font-nunito text-lg text-gray-600 max-w-2xl mx-auto">

            Sessions are designed to fit the family's schedule

          </p>

        </motion.div>



        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

          {/* School Year Sessions */}

          <motion.div

            initial={{ opacity: 0, x: -30 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="bg-white rounded-3xl p-8 shadow-lg border-2 border-sky-blue/20"

          >

            <div className="w-14 h-14 bg-sky-blue/20 rounded-2xl flex items-center justify-center mb-6">

              <BookOpen className="w-7 h-7 text-sky-blue" />

            </div>

            <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-3">

              School Year Tutoring & Enrichment

            </h3>

            <p className="font-nunito text-gray-600 mb-4">

              During the school year, sessions are available:

            </p>

            <div className="bg-cream rounded-2xl p-4">

              <p className="font-nunito font-semibold text-gray-800">Monday - Friday</p>

              <p className="font-fredoka text-2xl text-sunset-orange">3:00 PM - 7:00 PM</p>

            </div>

          </motion.div>



          {/* Summer Sessions */}

          <motion.div

            initial={{ opacity: 0, x: 30 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="bg-white rounded-3xl p-8 shadow-lg border-2 border-sunny-yellow/20"

          >

            <div className="w-14 h-14 bg-sunny-yellow/20 rounded-2xl flex items-center justify-center mb-6">

              <Sparkles className="w-7 h-7 text-sunny-yellow" />

            </div>

            <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-3">

              Summer Sessions

            </h3>

            <p className="font-nunito text-gray-600 mb-4">

              Summer learning continues with special sessions:

            </p>

            <div className="bg-cream rounded-2xl p-4 space-y-2">

              <div>

                <p className="font-nunito font-semibold text-gray-800">Mon & Wed</p>

                <p className="font-fredoka text-2xl text-sunset-orange">9:00 AM – 5:00 PM</p>

              </div>

              <div className="pt-2 border-t border-sunny-yellow/40">

                <p className="font-nunito font-semibold text-gray-800">Saturday</p>

                <p className="font-fredoka text-2xl text-sunset-orange">10:00 AM – 2:00 PM</p>

              </div>

            </div>

          </motion.div>

        </div>

      </div>

    </section>

  );

}



// Meet Your Tutor Section

function TutorSection() {

  return (

    <section className="py-12 md:py-16 bg-white relative overflow-hidden" data-testid="tutor-section">

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <div className="grid md:grid-cols-2 gap-8 items-center">

          {/* Tutor Image */}

          <motion.div

            initial={{ opacity: 0, x: -50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="relative flex justify-center md:justify-start order-2 md:order-1"

          >

            <div className="relative">

              {/* Decorative circles */}

              <motion.div 

                className="absolute -top-6 -left-6 w-24 h-24 bg-sunny-yellow/30 rounded-full"

                animate={{ scale: [1, 1.1, 1] }}

                transition={{ duration: 4, repeat: Infinity }}

              />

              <motion.div 

                className="absolute -bottom-4 -right-4 w-20 h-20 bg-apple-green/30 rounded-full"

                animate={{ scale: [1, 1.15, 1] }}

                transition={{ duration: 5, repeat: Infinity }}

              />



              {/* Tutor image - full display */}

              <div className="relative max-w-sm rounded-3xl overflow-hidden shadow-2xl border-4 border-white">

                <img 

                  src={IMAGES.tutor} 

                  alt="Tracey Cravens - Your Expert Tutor" 

                  className="w-full h-auto"

                  loading="lazy"

                  data-testid="tutor-image"

                />

              </div>



              {/* Floating badge */}

              <motion.div

                initial={{ opacity: 0, scale: 0 }}

                whileInView={{ opacity: 1, scale: 1 }}

                viewport={{ once: true }}

                transition={{ delay: 0.5 }}

                className="absolute -bottom-4 -right-4 bg-white px-5 py-3 rounded-2xl shadow-xl"

              >

                <div className="flex items-center gap-3">

                  <div className="w-10 h-10 bg-apple-green/20 rounded-full flex items-center justify-center">

                    <GraduationCap className="w-5 h-5 text-apple-green" />

                  </div>

                  <div>

                    <p className="font-fredoka font-bold text-gray-800 text-sm">Tracey Cravens</p>

                    <p className="font-nunito text-xs text-gray-600">Founder & Lead Tutor</p>

                  </div>

                </div>

              </motion.div>



              {/* Floating icon */}

              <FloatingIcon 

                icon={Heart} 

                className="-top-3 -right-3" 

                color="text-sunset-orange"

                bgColor="bg-white"

                animationType="bounce" 

                size={40} 

              />

            </div>

          </motion.div>



          {/* Content */}

          <motion.div

            initial={{ opacity: 0, x: 50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="order-1 md:order-2"

          >

            <span className="inline-block bg-sunset-orange/20 text-sunset-orange px-4 py-2 rounded-full font-nunito font-semibold mb-4">

              Meet Tracey Cravens

            </span>

            <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-gray-800 mb-6">

              Hi, I'm Tracey!

            </h2>

            <p className="font-nunito text-lg text-gray-600 mb-6">

              I'm an experienced K–8 certified educator with over 25 years in the classroom. I hold a Bachelor of Science in Business Administration and a Master of Arts in Teaching. I specialize in small-group tutoring for K–2 students, focusing on building strong foundations in reading and math. My approach blends learning with play, creating a fun, engaging environment where students grow in confidence and truly shine.

            </p>



            <div className="grid grid-cols-2 gap-4 mb-6">

              {[

                { number: "25+", label: "Years Teaching" },

                { number: "K-8", label: "Certified" },

                { number: "MA", label: "In Teaching" },

                { number: "K-2", label: "Specialty" }

              ].map((stat, index) => (

                <motion.div

                  key={index}

                  initial={{ opacity: 0, y: 20 }}

                  whileInView={{ opacity: 1, y: 0 }}

                  viewport={{ once: true }}

                  transition={{ delay: index * 0.1 }}

                  className="bg-cream rounded-2xl p-4 text-center"

                >

                  <p className="font-fredoka text-2xl font-bold text-sunset-orange">{stat.number}</p>

                  <p className="font-nunito text-sm text-gray-600">{stat.label}</p>

                </motion.div>

              ))}

            </div>



            <motion.button

              whileHover={{ scale: 1.05, y: -2 }}

              whileTap={{ scale: 0.95 }}

              className="bg-sunset-orange text-white px-8 py-3 rounded-full font-fredoka font-bold shadow-lg hover:shadow-xl transition-all"

              data-testid="tutor-cta"

            >

              Start Learning with Tracey

            </motion.button>

          </motion.div>

        </div>

      </div>

    </section>

  );

}



// Parent Section

function ParentSection() {

  return (

    <section id="parents" className="py-12 md:py-16 bg-cream relative overflow-hidden" data-testid="parent-section">

      {/* Subtle decoration */}

      <FloatingIcon 

        icon={Shield} 

        className="bottom-10 right-10 hidden md:flex opacity-50" 

        color="text-apple-green"

        bgColor="bg-white"

        animationType="pulse" 

        size={50} 

      />



      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <div className="grid md:grid-cols-2 gap-8 items-stretch">

          <motion.div

            initial={{ opacity: 0, x: -50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

            className="relative flex"

          >

            <div className="rounded-3xl overflow-hidden shadow-xl flex-1">

              <img 

                src={IMAGES.parentChild} 

                alt="Parent and child learning together" 

                className="w-full h-full object-cover"

                loading="lazy"

                data-testid="parent-child-image"

              />

            </div>

          </motion.div>



          <motion.div

            initial={{ opacity: 0, x: 50 }}

            whileInView={{ opacity: 1, x: 0 }}

            viewport={{ once: true }}

          >

            <span className="inline-block bg-apple-green/20 text-apple-green px-4 py-2 rounded-full font-nunito font-semibold mb-4">

              For Parents

            </span>

            <h2 className="font-fredoka text-3xl md:text-4xl font-bold text-gray-800 mb-6">

              Peace of Mind While Your Child Learns

            </h2>

            <p className="font-nunito text-lg text-gray-600 mb-8">

              I understand that your child's safety and development are your top priorities. That's why I've built Craven-it with parents in mind, ensuring a nurturing and secure learning environment.

            </p>



            <div className="space-y-6">

              {[

                { title: "Safe & Secure", desc: "Ad-free, child-friendly environment you can trust" },

                { title: "Progress Updates", desc: "Regular updates on your child's learning journey" },

                { title: "Personalized Attention", desc: "One-on-one focus tailored to your child's needs" }

              ].map((item, index) => (

                <motion.div

                  key={index}

                  initial={{ opacity: 0, x: 20 }}

                  whileInView={{ opacity: 1, x: 0 }}

                  viewport={{ once: true }}

                  transition={{ delay: index * 0.1 }}

                  className="flex items-start gap-4"

                >

                  <div className="w-8 h-8 bg-apple-green rounded-full flex items-center justify-center flex-shrink-0 mt-1">

                    <CheckCircle className="w-5 h-5 text-white" />

                  </div>

                  <div>

                    <h4 className="font-fredoka font-bold text-gray-800">{item.title}</h4>

                    <p className="font-nunito text-gray-600">{item.desc}</p>

                  </div>

                </motion.div>

              ))}

            </div>

          </motion.div>

        </div>

      </div>

    </section>

  );

}



// Testimonials Section

function TestimonialsSection() {
  const defaultTestimonials = [

    {

      name: "Sarah M.",

      role: "Parent of 1st Grader",

      text: "Tracey has been amazing with my daughter! Her reading skills have improved so much in just a few months. She actually looks forward to her tutoring sessions now!",

      rating: 5,

      initial: "S",

      accent: "from-[#FFE4E6] to-[#FFD5DC]",

      ring: "border-[#FD5E53]/30",

      bubble: "bg-[#FD5E53]",

    },

    {

      name: "Michael T.",

      role: "Parent of 2nd Grader",

      text: "My son was struggling with math, but Tracey's fun approach changed everything. He's now confident and excited about learning. Highly recommend!",

      rating: 5,

      initial: "M",

      accent: "from-[#DBEAFE] to-[#BAE6FD]",

      ring: "border-[#38BDF8]/30",

      bubble: "bg-[#38BDF8]",

    },

    {

      name: "Jennifer L.",

      role: "Parent of Kindergartener",

      text: "Tracey creates such a warm, encouraging environment. My child has grown so much in confidence. She truly makes learning feel like play!",

      rating: 5,

      initial: "J",

      accent: "from-[#FEF3C7] to-[#FDE68A]",

      ring: "border-[#F59E0B]/30",

      bubble: "bg-[#F59E0B]",

    },

    {

      name: "Kay J's Mom",

      role: "Proud Parent of Kay J",

      text: "Watching Kay J bloom under Mrs. Cravens's guidance has been the highlight of our year. Kay used to shy away from reading — now she can't put a book down! Tracey has a special way of making every child feel seen, celebrated, and capable of anything.",

      rating: 5,

      initial: "K",

      accent: "from-[#DCFCE7] to-[#BBF7D0]",

      ring: "border-[#22C55E]/30",

      bubble: "bg-[#22C55E]",

    },

  ];

  const [testimonials, setTestimonials] = useState(defaultTestimonials);

  useEffect(() => {
    let cancelled = false;

    const loadTestimonials = async () => {
      try {
        const response = await fetch('/api/testimonials/public');
        if (!response.ok) return;

        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0 || cancelled) return;

        const activeTestimonials = data
          .filter((item) => item.status === 'active' || item.status === undefined)
          .map((item, index) => ({
            name: item.name || 'Parent',
            role: [item.designation, item.company].filter(Boolean).join(', ') || 'Parent',
            text: item.message || '',
            rating: Number(item.rating) || 5,
            initial: (item.name || 'P').charAt(0).toUpperCase(),
            accent: [
              'from-[#FFE4E6] to-[#FFD5DC]',
              'from-[#DBEAFE] to-[#BAE6FD]',
              'from-[#FEF3C7] to-[#FDE68A]',
              'from-[#DCFCE7] to-[#BBF7D0]',
            ][index % 4],
            ring: [
              'border-[#FD5E53]/30',
              'border-[#38BDF8]/30',
              'border-[#F59E0B]/30',
              'border-[#22C55E]/30',
            ][index % 4],
            bubble: [
              'bg-[#FD5E53]',
              'bg-[#38BDF8]',
              'bg-[#F59E0B]',
              'bg-[#22C55E]',
            ][index % 4],
          }))
          .filter((item) => item.text.trim());

        if (!cancelled && activeTestimonials.length > 0) {
          setTestimonials(activeTestimonials);
        }
      } catch (error) {
        console.warn('Testimonials API unavailable:', error);
      }
    };

    loadTestimonials();

    return () => {
      cancelled = true;
    };
  }, []);



  return (

    <section className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#FFFBF5] to-white" data-testid="testimonials-section">

      {/* Floating decorative blobs */}

      <div className="absolute top-20 left-10 w-32 h-32 bg-[#FFE4E6] rounded-full blur-3xl opacity-50 pointer-events-none" />

      <div className="absolute bottom-32 right-10 w-40 h-40 bg-[#DBEAFE] rounded-full blur-3xl opacity-50 pointer-events-none" />



      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">

        {/* Header */}

        <motion.div

          initial={{ opacity: 0, y: 30 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="text-center mb-12 md:mb-14"

        >

          <span className="inline-flex items-center gap-2 bg-white border border-sunny-yellow/40 text-[#CA8A04] px-4 py-1.5 rounded-full font-nunito font-semibold text-sm mb-5 shadow-sm">

            <Star className="w-4 h-4 fill-sunny-yellow text-sunny-yellow" /> What Parents Say

          </span>

          <h2 className="font-fredoka text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-tight">

            Happy Families, <br className="md:hidden" />

            <span className="bg-gradient-to-r from-sunset-orange via-[#FB923C] to-[#F59E0B] bg-clip-text text-transparent">Happy Learners</span>

          </h2>

          <p className="font-nunito text-base md:text-lg text-gray-600 max-w-2xl mx-auto">

            Real stories from real parents — see how Tracey is helping K–2 students fall in love with learning.

          </p>

        </motion.div>



        {/* Featured Video Testimonials + Written Featured Review */}

        <motion.div

          initial={{ opacity: 0, y: 40 }}

          whileInView={{ opacity: 1, y: 0 }}

          viewport={{ once: true }}

          className="mb-16"

          data-testid="video-testimonial"

        >

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto items-stretch">

            {/* LEFT — Featured Story video */}

            <motion.div

              initial={{ opacity: 0, y: 30 }}

              whileInView={{ opacity: 1, y: 0 }}

              viewport={{ once: true }}

              whileHover={{ y: -4 }}

              className="relative h-full"

            >

              <div className="relative rounded-[2rem] p-[3px] bg-gradient-to-br from-sunset-orange/30 via-sky-blue/30 to-sunny-yellow/30 h-full">

                <div className="bg-white rounded-[1.85rem] p-3 md:p-4 h-full flex flex-col">

                  <div className="relative rounded-[1.5rem] overflow-hidden bg-gray-900 mx-auto flex-1" style={{ maxWidth: '360px' }}>

                    <video

                      src="https://customer-assets.emergentagent.com/job_animated-edu-kids/artifacts/rv3n9zir_video000000_1776696217833.mp4"

                      controls

                      preload="metadata"

                      playsInline

                      className="w-full h-full object-cover block"

                      data-testid="featured-testimonial-video"

                    >

                      Sorry, your browser doesn't support embedded videos.

                    </video>

                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-sunset-orange text-white text-[11px] font-fredoka font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">

                      <Star className="w-3 h-3 fill-white" /> Featured story

                    </span>

                  </div>

                  <p className="text-center font-fredoka text-gray-800 text-base mt-4 px-4 leading-snug">

                    A happy family shares their journey

                  </p>

                </div>

              </div>

            </motion.div>



            {/* RIGHT — Zoe's video + Featured written review */}

            <motion.div

              initial={{ opacity: 0, y: 30 }}

              whileInView={{ opacity: 1, y: 0 }}

              viewport={{ once: true }}

              transition={{ delay: 0.1 }}

              className="flex flex-col gap-5 h-full"

            >

              {/* Zoe's video */}

              <div className="relative rounded-[2rem] p-[3px] bg-gradient-to-br from-sunset-orange/30 via-sky-blue/30 to-sunny-yellow/30">

                <div className="bg-white rounded-[1.85rem] p-3 md:p-4">

                  <div className="relative rounded-[1.5rem] overflow-hidden bg-gray-900 mx-auto" style={{ maxWidth: '360px' }}>

                    <video

                      src="https://customer-assets.emergentagent.com/job_animated-edu-kids/artifacts/d2bg1vn8_zoe_2.mp4"

                      controls

                      preload="metadata"

                      playsInline

                      className="w-full h-auto block"

                      data-testid="featured-testimonial-video-2"

                    >

                      Sorry, your browser doesn't support embedded videos.

                    </video>

                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-sunset-orange text-white text-[11px] font-fredoka font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">

                      <Star className="w-3 h-3 fill-white" /> Zoe's story

                    </span>

                  </div>

                  <p className="text-center font-fredoka text-gray-800 text-base mt-4 px-4 leading-snug">

                    Zoe's learning adventure

                  </p>

                </div>

              </div>



              {/* Featured written review */}

              <div

                className="relative flex-1 rounded-[2rem] p-[3px] bg-gradient-to-br from-sunset-orange via-[#FB923C] to-sunny-yellow shadow-lg"

                data-testid="featured-written-review"

              >

                <div className="bg-gradient-to-br from-white via-[#FFFBF5] to-[#FEF3C7]/40 rounded-[1.85rem] p-6 h-full flex flex-col">

                  {/* Badge */}

                  <span className="self-start inline-flex items-center gap-1.5 bg-sunset-orange text-white text-[11px] font-fredoka font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md mb-3">

                    <Star className="w-3 h-3 fill-white" /> Featured Review

                  </span>



                  {/* Stars + Quote watermark */}

                  <div className="flex items-center justify-between mb-3">

                    <div className="flex gap-1">

                      {[...Array(5)].map((_, i) => (

                        <Star key={i} className="w-5 h-5 text-sunny-yellow fill-sunny-yellow drop-shadow-sm" />

                      ))}

                    </div>

                    <span aria-hidden="true" className="font-fredoka text-[64px] leading-none text-sunset-orange/15 select-none -mt-2">"</span>

                  </div>



                  {/* Testimonial text */}

                  <p className="font-nunito text-gray-700 leading-relaxed text-[14px] flex-1">

                    I would like to sincerely appreciate <span className="font-semibold text-gray-800">Mrs Cravens</span> for the dedication, patience, and passion she has shown throughout the school year. Her ability to create a supportive and engaging learning environment has made a remarkable difference in my child's academic growth and confidence.

                    <br /><br />

                    What stands out the most is her commitment to excellence and attention to each student's development. This played a significant role in my child's achievement of placing <span className="font-semibold text-sunset-orange">third runner-up in the district spelling bee</span> last year — an accomplishment we are incredibly proud of.

                    <br /><br />

                    Beyond academics, Mrs Cravens consistently encourages curiosity, discipline, and a love for learning, which has had a lasting impact on my child. I am truly grateful for her positive influence and guidance.

                  </p>



                  {/* Author */}

                  <div className="flex items-center gap-3 pt-4 mt-4 border-t border-sunny-yellow/40">

                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sunset-orange to-[#F59E0B] flex items-center justify-center text-white font-fredoka font-bold text-base shadow-md flex-shrink-0">

                      A

                    </div>

                    <div className="min-w-0">

                      <p className="font-fredoka font-bold text-gray-800 text-base leading-tight">A Grateful Parent</p>

                      <p className="font-nunito text-xs text-gray-600">Spelling Bee Champion's Family</p>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </motion.div>



        {/* Written Testimonials */}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

          {testimonials.map((t, index) => (

            <motion.div

              key={index}

              initial={{ opacity: 0, y: 30 }}

              whileInView={{ opacity: 1, y: 0 }}

              viewport={{ once: true }}

              transition={{ delay: index * 0.1 }}

              whileHover={{ y: -6 }}

              className={`relative bg-gradient-to-br ${t.accent} rounded-[2rem] p-7 md:p-8 border ${t.ring} transition-all hover:shadow-xl`}

              data-testid={`testimonial-${index}`}

            >

              {/* Large quote mark watermark */}

              <span aria-hidden="true" className="absolute top-3 right-6 font-fredoka text-[100px] leading-none text-white/50 select-none">

                "

              </span>



              {/* Stars */}

              <div className="flex gap-1 mb-4 relative">

                {[...Array(t.rating)].map((_, i) => (

                  <Star key={i} className="w-5 h-5 text-sunny-yellow fill-sunny-yellow drop-shadow-sm" />

                ))}

              </div>



              {/* Quote text */}

              <p className="font-nunito text-gray-800 leading-relaxed relative mb-7 text-[15px]">

                {t.text}

              </p>



              {/* Author row */}

              <div className="flex items-center gap-3 pt-5 border-t border-white/60 relative">

                <div className={`w-12 h-12 rounded-full ${t.bubble} flex items-center justify-center text-white font-fredoka font-bold text-lg shadow-md flex-shrink-0`}>

                  {t.initial}

                </div>

                <div className="min-w-0">

                  <p className="font-fredoka font-bold text-gray-800 text-base leading-tight">{t.name}</p>

                  <p className="font-nunito text-sm text-gray-600">{t.role}</p>

                </div>

              </div>

            </motion.div>

          ))}

        </div>

      </div>

    </section>

  );

}



// Footer

function Footer() {

  return (

    <footer className="relative bg-sky-blue pt-12 pb-6" data-testid="footer">

      {/* Wavy top border */}

      <div className="absolute -top-1 left-0 right-0">

        <svg viewBox="0 0 1440 60" fill="none" xmlns="http\://www\.w3.org/2000/svg" className="w-full">

          <path d="M0 60L60 52.5C120 45 240 30 360 22.5C480 15 600 15 720 22.5C840 30 960 45 1080 52.5C1200 60 1320 60 1380 60H1440V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V60Z" fill="#FFFDD0"/>

        </svg>

      </div>



      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">

        <div className="grid md:grid-cols-5 gap-6 mb-8">

          {/* Brand */}

          <div className="md:col-span-2">

            <div className="flex items-center gap-2 mb-4">

              <Sparkles className="w-8 h-8 text-white" />

              <span className="font-fredoka text-2xl font-bold text-white">Craven-it</span>

            </div>

            <p className="font-nunito text-white/90 mb-4 max-w-md">

              Created by Tracey Cravens - Making learning fun and engaging for kids. Join me on this exciting educational adventure!

            </p>

            <p className="font-nunito text-white/80 text-sm mb-6">

              Have questions? Reach out anytime!

            </p>

            <div className="flex gap-4">

              {/* Social icons placeholder - using simple circles */}

              {['facebook', 'twitter', 'instagram', 'youtube'].map((social) => (

                <motion.a

                  key={social}

                  href="#"

                  whileHover={{ scale: 1.1, y: -3 }}

                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"

                >

                  <span className="sr-only">{social}</span>

                  <div className="w-5 h-5 bg-white/80 rounded-full" />

                </motion.a>

              ))}

            </div>

          </div>



          {/* Links */}

          <div>

            <h4 className="font-fredoka font-bold text-white mb-4">Quick Links</h4>

            <ul className="space-y-3">

              {['About Tracey', 'Features', 'Lessons', 'Contact'].map((link) => (

                <li key={link}>

                  <a href="#" className="font-nunito text-white/90 hover:text-white transition-colors">{link}</a>

                </li>

              ))}

            </ul>

          </div>



          <div>

            <h4 className="font-fredoka font-bold text-white mb-4">Contact</h4>

            <ul className="space-y-3">

              <li>

                <a
                  href="tel:+13133853090"
                  className="font-nunito text-white/90 hover:text-white transition-colors inline-flex items-center gap-2"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4" /> (313) 385-3090
                </a>

              </li>

              <li>

                <a
                  href="mailto:admin@cravenitk2.com"
                  className="font-nunito text-white/90 hover:text-white transition-colors inline-flex items-center gap-2"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4" /> admin@cravenitk2.com
                </a>

              </li>

            </ul>

          </div>



          <div>

            <h4 className="font-fredoka font-bold text-white mb-4">Support</h4>

            <ul className="space-y-3">

              <li>

                <a

                  href="/Craven-it-User-Guide.pdf"

                  download="Craven-it-User-Guide.pdf"

                  className="font-nunito text-white/90 hover:text-white transition-colors inline-flex items-center gap-1.5"

                  data-testid="footer-download-guide"

                >

                  Download User Guide (PDF)

                </a>

              </li>

              {['Help Center', 'Privacy Policy', 'Terms of Service', 'FAQ'].map((link) => (

                <li key={link}>

                  <a href="#" className="font-nunito text-white/90 hover:text-white transition-colors">{link}</a>

                </li>

              ))}

            </ul>

          </div>

        </div>



        {/* Bottom */}

        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">

          <p className="font-nunito text-white/80 text-sm">

            © 2024 Craven-it by Tracey Cravens. All rights reserved.

          </p>



          {/* Animated icons */}

          <div className="flex gap-3">

            <motion.div

              className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center"

              animate={{ rotate: [0, 10, -10, 0] }}

              transition={{ duration: 2, repeat: Infinity }}

            >

              <Heart className="w-5 h-5 text-white" />

            </motion.div>

            <motion.div

              className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center"

              animate={{ y: [0, -5, 0] }}

              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}

            >

              <Star className="w-5 h-5 text-white" />

            </motion.div>

            <motion.div

              className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center"

              animate={{ rotate: [0, -10, 10, 0] }}

              transition={{ duration: 2.5, repeat: Infinity }}

            >

              <Sparkles className="w-5 h-5 text-white" />

            </motion.div>

          </div>

        </div>

      </div>

    </footer>

  );

}



// Main Landing Page Component

export default function LandingPage() {

  const { user, logout } = useAuth();

  const [authModalOpen, setAuthModalOpen] = useState(false);

  const [authMode, setAuthMode] = useState('login');



  const handleAuthClick = (mode) => {

    setAuthMode(mode);

    setAuthModalOpen(true);

  };



  const handleLogout = async () => {

    await logout();

  };



  return (

    <div className="min-h-screen" data-testid="landing-page">

      <Navigation 

        onAuthClick={handleAuthClick} 

        user={user} 

        onLogout={handleLogout} 

      />

      <HeroSection onAuthClick={handleAuthClick} />

      <FeaturesSection />

      <GamificationSection />

      <HowItWorksSection onAuthClick={handleAuthClick} />

      <ScheduleSection />

      <TutorSection />

      <ParentSection />

      <TestimonialsSection />

      <Footer />



      <AuthModals 

        isOpen={authModalOpen} 

        onClose={() => setAuthModalOpen(false)} 

        initialMode={authMode}

      />

    </div>

  );

}
